import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import axios from 'axios';
import AudioPlayer from './AudioPlayer';

// Helper function to clean up any markdown formatting that slips through
const cleanMarkdown = (text) => {
  if (!text) return '';
  return text
    // Convert **bold** to just the text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    // Convert *italic* to just the text
    .replace(/\*([^*]+)\*/g, '$1')
    // Convert _italic_ to just the text
    .replace(/_([^_]+)_/g, '$1')
    // Clean up any remaining stray asterisks
    .replace(/\*+/g, '');
};

const StoryBook = ({ page, formToken, takenChoices = {}, customPaths = [], storyId, storyOwnerId, canEnd: initialCanEnd = false, pageCount: initialPageCount = 0, isCompleted: initialIsCompleted = false, isPublic: initialIsPublic = false, canCreatePaths = true, isOwner = false, isSignedIn = true, currentUserId, narrators = [], currentNarratorId, currentNarratorName, currentNarratorFlag, targetLanguage = 'english', baseLanguage = 'english', vocabWords = [], isLearningMode = false, pathDepth }) => {

  // Use ref to track initial mount
  const componentMounted = useRef(false);
  const [currentPage, setCurrentPage] = useState(page);
  const [loading, setLoading] = useState(page.status !== 'completed');
  const [error, setError] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(page.audio_url);

  // Narrator state
  const [selectedNarrator, setSelectedNarrator] = useState(currentNarratorId);
  const [narratorName, setNarratorName] = useState(currentNarratorName);
  const [narratorFlag, setNarratorFlag] = useState(currentNarratorFlag);
  const [showNarratorPicker, setShowNarratorPicker] = useState(false);
  const [narratorChanging, setNarratorChanging] = useState(false);
  const [previewAudioUrl, setPreviewAudioUrl] = useState(null);
  const previewAudioRef = useRef(null);

  // End story state
  const [canEnd, setCanEnd] = useState(initialCanEnd);
  const [showEndOptions, setShowEndOptions] = useState(false);
  const [customEnding, setCustomEnding] = useState('');
  const [endingBook, setEndingBook] = useState(false);
  const [isCompleted, setIsCompleted] = useState(initialIsCompleted);
  const [isPublic, setIsPublic] = useState(initialIsPublic);
  const [sharingBook, setSharingBook] = useState(false);

  // Button loading state
  const [submitting, setSubmitting] = useState(false);
  const [selectedChoiceIndex, setSelectedChoiceIndex] = useState(null);

  // Add word tracking states
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [currentCharIndex, setCurrentCharIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioTimestamps, setAudioTimestamps] = useState(null);
  const wordsRef = useRef([]);

  // Shadow reading mode state
  const audioPlayerRef = useRef(null);
  const [shadowMode, setShadowMode] = useState(false);
  const [activeSentenceIndex, setActiveSentenceIndex] = useState(-1);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [sentenceTimings, setSentenceTimings] = useState([]);
  const [playedSentences, setPlayedSentences] = useState(new Set());
  const [autoAdvance, setAutoAdvance] = useState(false);
  const autoAdvanceTimerRef = useRef(null);
  const playSentenceRef = useRef(null);

  // T6: Share snippet state
  const [snippetCopied, setSnippetCopied] = useState(false);

  // Learning mode states (T3: explain, T4: vocab, T5: translation)
  const [showTranslation, setShowTranslation] = useState(false);
  const [translatedParagraphs, setTranslatedParagraphs] = useState(null);
  const [translatedChoices, setTranslatedChoices] = useState(null);
  const [translationLoading, setTranslationLoading] = useState(false);
  const [explainPopover, setExplainPopover] = useState(null); // { sentence, x, y, data, loading }
  const explainCache = useRef({}); // Cache explanations per sentence
  const [expandedChoiceTranslations, setExpandedChoiceTranslations] = useState({}); // { index: true/false }

  // Comprehension questions state
  const [comprehensionQuestions, setComprehensionQuestions] = useState([]);
  const [comprehensionLoading, setComprehensionLoading] = useState(false);
  const [questionsAnswered, setQuestionsAnswered] = useState({}); // { questionId: { correct, answered } }
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [answerFeedback, setAnswerFeedback] = useState(null); // { correct, correctAnswer, xpEarned }
  const [userProficiency, setUserProficiency] = useState(null);
  const [blockChoicesUntilAnswered, setBlockChoicesUntilAnswered] = useState(true);

  // Discussion chat state
  const [showDiscussion, setShowDiscussion] = useState(false);
  const [discussionChatId, setDiscussionChatId] = useState(null);
  const [discussionMessages, setDiscussionMessages] = useState([]);
  const [discussionInput, setDiscussionInput] = useState('');
  const [discussionLoading, setDiscussionLoading] = useState(false);
  const [discussionInitializing, setDiscussionInitializing] = useState(false);
  const discussionEndRef = useRef(null);

  // Fetch comprehension questions when in learning mode
  useEffect(() => {
    if (!isLearningMode || !isSignedIn || !currentUserId) return;

    const fetchQuestions = async () => {
      setComprehensionLoading(true);
      try {
        const response = await axios.get(`/adventure_story_pages/${page.id}/comprehension_questions`, {
          headers: { 'X-CSRF-Token': formToken }
        });
        setComprehensionQuestions(response.data.questions || []);
        setUserProficiency(response.data.user_proficiency);

        // Mark already answered questions
        const answered = {};
        (response.data.questions || []).forEach(q => {
          if (q.user_progress && q.user_progress.status !== 'pending') {
            answered[q.id] = { correct: q.user_progress.status === 'correct', answered: true };
          }
        });
        setQuestionsAnswered(answered);
      } catch (err) {
        console.error('Error fetching comprehension questions:', err);
      }
      setComprehensionLoading(false);
    };

    fetchQuestions();
  }, [isLearningMode, isSignedIn, currentUserId, page.id, formToken]);

  // Check if all questions are answered
  const allQuestionsAnswered = comprehensionQuestions.length > 0 &&
    comprehensionQuestions.every(q => questionsAnswered[q.id]?.answered);

  const retryCount = useRef(0);
  const maxRetries = useRef(10);
  const baseDelay = useRef(3000); // Start with 3 seconds

  // Add custom scrollbar styles to the document head
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .custom-scrollbar::-webkit-scrollbar {
        width: 8px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: #f9fafb;
        border-radius: 10px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 10px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: #9ca3af;
      }
    `;
    document.head.appendChild(style);

    componentMounted.current = true;

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Make sure we update audioUrl if page.audio_url changes
  useEffect(() => {
    if (page.audio_url) {
      setAudioUrl(page.audio_url);
    }
  }, [page.audio_url]);

  // Reset audio state when page changes
  useEffect(() => {
    setAudioUrl(page.audio_url || null);
    setAudioTimestamps(page.audio_timestamps || null);
    setCurrentWordIndex(-1);
    setCurrentCharIndex(-1);
    setIsPlaying(false);
    setAudioLoading(false);
    // Reset shadow mode state
    setShadowMode(false);
    setActiveSentenceIndex(-1);
    setSentenceTimings([]);
    setPlayedSentences(new Set());
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
  }, [page.id]);

  useEffect(() => {
    // Only poll if we need to (page not completed yet)
    if (!currentPage || currentPage.status === 'completed') return;

    let isMounted = true;
    let timeoutId = null;

    const checkPageStatus = async () => {
      if (!isMounted) return;

      try {
        const response = await axios.get(`/adventure_story_pages/${currentPage.id}/check_status`, {
          headers: {
            'X-CSRF-Token': formToken
          }
        });

        if (!isMounted) return;

        // Only update if data actually changed
        if (response.data.status !== currentPage.status ||
            response.data.content !== currentPage.content ||
            response.data.image_url !== currentPage.image_url) {
          setCurrentPage(response.data);
        }

        // Reset retry count on success (but don't clear non-existent errors)
        retryCount.current = 0;

        if (response.data.status === 'completed') {
          setLoading(false);
        } else {
          // Continue polling
          const delay = Math.min(baseDelay.current * Math.pow(1.5, retryCount.current), 15000);
          timeoutId = setTimeout(checkPageStatus, delay);
        }
      } catch (err) {
        if (!isMounted) return;

        console.error('Error checking page status:', err);
        retryCount.current += 1;

        if (retryCount.current > maxRetries.current) {
          setError('Could not check page status after several attempts. Please refresh the page.');
          setLoading(false);
        } else {
          // Only show retry message after 3+ failures to avoid flicker
          if (retryCount.current >= 3) {
            setError(`Connection issue. Retrying... (${retryCount.current}/${maxRetries.current})`);
          }
          // Keep trying with exponential backoff
          const delay = Math.min(baseDelay.current * Math.pow(1.5, retryCount.current), 15000);
          timeoutId = setTimeout(checkPageStatus, delay);
        }
      }
    };

    // Start polling after initial delay
    timeoutId = setTimeout(checkPageStatus, 1000);

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [currentPage?.id, currentPage?.status, formToken]);

    const generateAudio = async (e, forceRegenerate = false) => {
    // Prevent default behavior
    if (e) e.preventDefault();

    setAudioLoading(true);

    try {
      const requestBody = forceRegenerate ? { force_regenerate: true } : {};
      const response = await axios.post(`/adventure_story_pages/${currentPage.id}/generate_audio`, requestBody, {
        headers: {
          'X-CSRF-Token': formToken
        }
      });

      if (response.data.success) {

        setAudioUrl(response.data.audio_url);
        setAudioTimestamps(response.data.audio_timestamps);
        // Reset word tracking state
        setCurrentWordIndex(-1);
        setCurrentCharIndex(-1);
        setIsPlaying(false);
      } else {
        setError('Failed to generate audio. Please try again.');
      }
    } catch (error) {
      console.error('Error generating audio:', error);
      setError('Failed to generate audio. Please try again.');
    } finally {
      setAudioLoading(false);
    }
  };

  // Split text into words and calculate timing
  const splitIntoWords = (text) => {
    return text.split(/(\s+)/); // Keep whitespace for proper spacing
  };

  // Handle audio time updates for word tracking
  const handleTimeUpdate = useCallback((audioElement) => {
    try {
      if (!audioElement) {
        return;
      }

      const currentTime = audioElement.currentTime;
      const audioIsPlaying = !audioElement.paused;

      // Update playing state based on actual audio state
      if (audioIsPlaying !== isPlaying) {
        setIsPlaying(audioIsPlaying);
      }

      // Shadow mode: detect sentence end and auto-pause
      if (shadowMode && activeSentenceIndex >= 0 && audioIsPlaying && sentenceTimings[activeSentenceIndex]) {
        const timing = sentenceTimings[activeSentenceIndex];
        // Add a small buffer (0.05s) to ensure the last character is heard
        if (currentTime >= timing.endTime + 0.05) {
          audioElement.pause();
          setIsPlaying(false);
          // Mark this sentence as played
          setPlayedSentences(prev => {
            const next = new Set(prev);
            next.add(activeSentenceIndex);
            return next;
          });
          // Auto-advance to next sentence after delay
          if (autoAdvance && activeSentenceIndex < sentenceTimings.length - 1) {
            autoAdvanceTimerRef.current = setTimeout(() => {
              if (playSentenceRef.current) {
                playSentenceRef.current(activeSentenceIndex + 1);
              }
            }, 2000);
          }
          return;
        }
      }

      if (audioTimestamps && audioIsPlaying && audioTimestamps.character_start_times_seconds) {
        // Use precise character-level timestamps
        const { characters, character_start_times_seconds } = audioTimestamps;

        // Find the current character index based on timestamp
        let newCharIndex = -1;
        for (let i = 0; i < (character_start_times_seconds?.length || 0); i++) {
          if (currentTime >= character_start_times_seconds[i]) {
            newCharIndex = i;
          } else {
            break;
          }
        }

        if (newCharIndex !== currentCharIndex) {
          setCurrentCharIndex(newCharIndex);

          // Also update word index for fallback highlighting
          const currentText = characters.slice(0, newCharIndex + 1).join('');
          const currentWordCount = currentText.split(/\s+/).filter(word => word.length > 0).length;
          setCurrentWordIndex(Math.max(0, currentWordCount - 1));
        }
      } else if (!audioTimestamps && audioIsPlaying) {
        // Fall back to estimated word timing
        const duration = audioElement.duration;

        if (duration && wordsRef.current?.length > 0) {
          const speechStartDelay = 0.5;
          const speechEndBuffer = 2.0;
          const effectiveDuration = Math.max(1, duration - speechStartDelay - speechEndBuffer);
          const adjustedTime = Math.max(0, currentTime - speechStartDelay);

          const progress = Math.min(1, adjustedTime / effectiveDuration);
          const actualWords = (wordsRef.current || []).filter(word => !/^\s+$/.test(word));
          const conservativeProgress = progress * 0.85;
          const estimatedActualWordIndex = Math.floor(conservativeProgress * actualWords.length);

          let actualWordCount = 0;
          let estimatedWordIndex = -1;
          const currentWords = wordsRef.current || [];
          for (let i = 0; i < currentWords.length; i++) {
            if (!/^\s+$/.test(currentWords[i])) {
              if (actualWordCount === estimatedActualWordIndex) {
                estimatedWordIndex = i;
                break;
              }
              actualWordCount++;
            }
          }

          if (estimatedWordIndex !== currentWordIndex && estimatedWordIndex >= 0) {
            setCurrentWordIndex(estimatedWordIndex);
          }
        }
      }
    } catch (error) {
      console.error('Error in handleTimeUpdate:', error);
      return;
    }
  }, [audioTimestamps, isPlaying, currentCharIndex, currentWordIndex, shadowMode, activeSentenceIndex, sentenceTimings, autoAdvance]);

  // Handle audio play/pause events from AudioPlayer
  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentWordIndex(-1);
    setCurrentCharIndex(-1);
    if (shadowMode) {
      // Mark last sentence as played
      if (activeSentenceIndex >= 0) {
        setPlayedSentences(prev => {
          const next = new Set(prev);
          next.add(activeSentenceIndex);
          return next;
        });
      }
    }
  }, [shadowMode, activeSentenceIndex]);

  const handleLoadedData = useCallback(() => {
    // Audio loaded, ready to play
  }, []);

  // Handle narrator selection
  const handleNarratorSelect = async (narrator) => {
    if (narratorChanging) return;

    setNarratorChanging(true);

    // Stop preview audio if playing
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.currentTime = 0;
    }
    setPreviewAudioUrl(null);

    try {
      const response = await axios.patch(`/adventure_stories/${storyId}/update_narrator`, {
        narrator_id: narrator?.id || null
      }, {
        headers: {
          'X-CSRF-Token': formToken
        }
      });

      if (response.data.success) {
        setSelectedNarrator(narrator?.id || null);
        setNarratorName(narrator?.name || null);
        setNarratorFlag(narrator?.flag || null);
        setShowNarratorPicker(false);

        // Clear existing audio so user regenerates with new voice
        if (audioUrl && narrator?.id !== selectedNarrator) {
          setAudioUrl(null);
          setAudioTimestamps(null);
        }
      }
    } catch (error) {
      console.error('Error updating narrator:', error);
    } finally {
      setNarratorChanging(false);
    }
  };

  // Play narrator preview
  const playNarratorPreview = (previewUrl) => {
    if (previewAudioRef.current && previewAudioUrl === previewUrl) {
      // Toggle play/pause
      if (previewAudioRef.current.paused) {
        previewAudioRef.current.play();
      } else {
        previewAudioRef.current.pause();
      }
    } else {
      // Start new preview
      setPreviewAudioUrl(previewUrl);
      setTimeout(() => {
        if (previewAudioRef.current) {
          previewAudioRef.current.play();
        }
      }, 100);
    }
  };

  // T3: Click-to-explain a sentence
  const handleSentenceClick = useCallback(async (sentence, event) => {
    if (!isLearningMode || !sentence.trim()) return;

    const rect = event.target.getBoundingClientRect();

    // Check cache first
    if (explainCache.current[sentence]) {
      setExplainPopover({
        sentence,
        x: rect.left,
        y: rect.bottom,
        data: explainCache.current[sentence],
        loading: false
      });
      return;
    }

    setExplainPopover({ sentence, x: rect.left, y: rect.bottom, data: null, loading: true });

    try {
      const response = await axios.post(
        `/adventure_story_pages/${currentPage.id}/explain`,
        { sentence },
        { headers: { 'X-CSRF-Token': formToken } }
      );
      explainCache.current[sentence] = response.data;
      setExplainPopover(prev => prev?.sentence === sentence ? { ...prev, data: response.data, loading: false } : prev);
    } catch (err) {
      console.error('Explain error:', err);
      setExplainPopover(prev => prev?.sentence === sentence ? { ...prev, loading: false, data: { translation: 'Could not load explanation', grammar_note: '', examples: [] } } : prev);
    }
  }, [isLearningMode, currentPage?.id, formToken]);

  // T5: Fetch full page translation
  const fetchTranslation = useCallback(async () => {
    if (translatedParagraphs || translationLoading) return;
    setTranslationLoading(true);
    try {
      const response = await axios.post(
        `/adventure_story_pages/${currentPage.id}/translate`,
        {},
        { headers: { 'X-CSRF-Token': formToken } }
      );
      setTranslatedParagraphs(response.data.paragraphs || []);
      setTranslatedChoices(response.data.choices || []);
    } catch (err) {
      console.error('Translation error:', err);
    }
    setTranslationLoading(false);
  }, [currentPage?.id, formToken, translatedParagraphs, translationLoading]);

  // Toggle translation mode
  const toggleTranslation = useCallback(() => {
    const next = !showTranslation;
    setShowTranslation(next);
    if (next && !translatedParagraphs) {
      fetchTranslation();
    }
  }, [showTranslation, translatedParagraphs, fetchTranslation]);

  // Toggle inline translation for a single choice (tap translate icon next to choice)
  const toggleChoiceTranslation = useCallback(async (index) => {
    // If already expanded, just collapse it
    if (expandedChoiceTranslations[index]) {
      setExpandedChoiceTranslations(prev => ({ ...prev, [index]: false }));
      return;
    }
    // If we already have translations, just expand
    if (translatedChoices && translatedChoices[index]) {
      setExpandedChoiceTranslations(prev => ({ ...prev, [index]: true }));
      return;
    }
    // Otherwise fetch translations first
    if (!translatedChoices && !translationLoading) {
      await fetchTranslation();
    }
    setExpandedChoiceTranslations(prev => ({ ...prev, [index]: true }));
  }, [expandedChoiceTranslations, translatedChoices, translationLoading, fetchTranslation]);

  // Comprehension: submit answer
  const submitComprehensionAnswer = useCallback(async (questionId, answer) => {
    try {
      const response = await axios.post(`/adventure_story_pages/${page.id}/comprehension_answers`,
        { question_id: questionId, answer },
        { headers: { 'X-CSRF-Token': formToken } }
      );

      const { correct, correct_answer, xp_earned, total_xp, level } = response.data;

      setQuestionsAnswered(prev => ({
        ...prev,
        [questionId]: { correct, answered: true }
      }));

      setAnswerFeedback({ correct, correctAnswer: correct_answer, xpEarned: xp_earned });

      if (userProficiency) {
        setUserProficiency({ ...userProficiency, xp_points: total_xp, level });
      }

      // Move to next question after delay
      setTimeout(() => {
        setAnswerFeedback(null);
        if (currentQuestionIndex < comprehensionQuestions.length - 1) {
          setCurrentQuestionIndex(prev => prev + 1);
        }
      }, 1500);

      return { correct, xpEarned };
    } catch (err) {
      console.error('Error submitting answer:', err);
      return { correct: false, xpEarned: 0 };
    }
  }, [page.id, formToken, currentQuestionIndex, comprehensionQuestions.length, userProficiency]);

  // Auto-scroll discussion to bottom when messages change
  useEffect(() => {
    if (discussionEndRef.current) {
      discussionEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [discussionMessages]);

  // Discussion: start or resume a discussion chat
  const startDiscussion = useCallback(async () => {
    if (discussionChatId) {
      setShowDiscussion(true);
      return;
    }

    setDiscussionInitializing(true);
    setShowDiscussion(true);
    try {
      const response = await axios.post(`/adventure_story_pages/${page.id}/start_discussion`, {}, {
        headers: { 'X-CSRF-Token': formToken }
      });
      setDiscussionChatId(response.data.chat_id);
      setDiscussionMessages(response.data.messages || []);
    } catch (err) {
      console.error('Error starting discussion:', err);
    }
    setDiscussionInitializing(false);
  }, [page.id, formToken, discussionChatId]);

  // Discussion: send a message
  const sendDiscussionMessage = useCallback(async (messageText) => {
    const content = (messageText || discussionInput).trim();
    if (!content || discussionLoading) return;

    // Optimistic: add user message immediately
    const tempUserMsg = { id: `temp-${Date.now()}`, role: 'user', content };
    setDiscussionMessages(prev => [...prev, tempUserMsg]);
    setDiscussionInput('');
    setDiscussionLoading(true);

    try {
      const response = await axios.post(`/adventure_story_pages/${page.id}/send_discussion_message`,
        { content },
        { headers: { 'X-CSRF-Token': formToken } }
      );

      // Replace temp message and add assistant response
      setDiscussionMessages(prev => {
        const withoutTemp = prev.filter(m => m.id !== tempUserMsg.id);
        return [
          ...withoutTemp,
          { id: `user-${Date.now()}`, role: 'user', content },
          {
            ...response.data,
            role: 'assistant',
            follow_up_questions: response.data.follow_up_questions || [],
            vocabulary_highlight: response.data.vocabulary_highlight || []
          }
        ];
      });
    } catch (err) {
      console.error('Error sending discussion message:', err);
      // Remove the optimistic message on error
      setDiscussionMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
    }
    setDiscussionLoading(false);
  }, [page.id, formToken, discussionInput, discussionLoading]);

  // T6: Copy snippet link to clipboard
  const handleShareSnippet = useCallback(() => {
    const snippetUrl = `${window.location.origin}/story-snippets/${storyId}/${currentPage.page_number}`;
    navigator.clipboard.writeText(snippetUrl).then(() => {
      setSnippetCopied(true);
      setTimeout(() => setSnippetCopied(false), 2000);
    }).catch(() => {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = snippetUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setSnippetCopied(true);
      setTimeout(() => setSnippetCopied(false), 2000);
    });
  }, [storyId, currentPage?.page_number]);

  // Close popover on click outside
  useEffect(() => {
    if (!explainPopover) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.explain-popover') && !e.target.closest('.clickable-sentence')) {
        setExplainPopover(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [explainPopover]);

  // T4: Helper — check if a word is a vocab word (case-insensitive)
  const isVocabWord = useCallback((word) => {
    if (!vocabWords || vocabWords.length === 0) return false;
    const clean = word.replace(/[.,!?;:"""''()[\]{}]/g, '').toLowerCase();
    return vocabWords.some(v => v.toLowerCase() === clean);
  }, [vocabWords]);

  // Helper — split text into sentences (handles ., !, ?, and CJK sentence enders)
  const splitIntoSentences = (text) => {
    return text.split(/(?<=[.!?\u3002\uFF01\uFF1F])\s+/).filter(Boolean);
  };

  // Build sentence timings from audio timestamps
  const buildSentenceTimings = useCallback(() => {
    if (!audioTimestamps?.characters || !audioTimestamps?.character_start_times_seconds || !currentPage?.content) {
      return [];
    }

    let text = cleanMarkdown(currentPage.content);
    text = text.replace(/\[\[([^\]]+)\]\]/g, (_, word) => word);

    // Flatten all sentences from all paragraphs
    const paragraphs = text.split(/\n\n+/).filter(Boolean);
    const allSentences = [];
    paragraphs.forEach(para => {
      splitIntoSentences(para).forEach(s => allSentences.push(s));
    });

    const { characters, character_start_times_seconds, character_end_times_seconds } = audioTimestamps;
    const fullText = characters.join('');
    const timings = [];
    let searchFrom = 0;

    allSentences.forEach((sentence, idx) => {
      // Find this sentence in the character array text
      const sentenceClean = sentence.trim();
      const pos = fullText.indexOf(sentenceClean, searchFrom);

      if (pos === -1) {
        // Fallback: try to find the first few words
        const firstWords = sentenceClean.substring(0, Math.min(20, sentenceClean.length));
        const fallbackPos = fullText.indexOf(firstWords, searchFrom);
        if (fallbackPos !== -1) {
          const startIdx = fallbackPos;
          const endIdx = Math.min(fallbackPos + sentenceClean.length - 1, characters.length - 1);
          timings.push({
            index: idx,
            text: sentence,
            startCharIdx: startIdx,
            endCharIdx: endIdx,
            startTime: character_start_times_seconds[startIdx] || 0,
            endTime: (character_end_times_seconds ? character_end_times_seconds[endIdx] : character_start_times_seconds[endIdx]) || 0,
          });
          searchFrom = fallbackPos + sentenceClean.length;
        }
        return;
      }

      const startIdx = pos;
      const endIdx = Math.min(pos + sentenceClean.length - 1, characters.length - 1);
      timings.push({
        index: idx,
        text: sentence,
        startCharIdx: startIdx,
        endCharIdx: endIdx,
        startTime: character_start_times_seconds[startIdx] || 0,
        endTime: (character_end_times_seconds ? character_end_times_seconds[endIdx] : character_start_times_seconds[endIdx]) || 0,
      });
      searchFrom = pos + sentenceClean.length;
    });

    return timings;
  }, [audioTimestamps, currentPage?.content]);

  // Rebuild sentence timings when timestamps change
  useEffect(() => {
    if (shadowMode && audioTimestamps) {
      const timings = buildSentenceTimings();
      setSentenceTimings(timings);
    }
  }, [shadowMode, audioTimestamps, buildSentenceTimings]);

  // Play a specific sentence
  const playSentence = useCallback((sentenceIndex) => {
    const player = audioPlayerRef.current;
    if (!player || !sentenceTimings[sentenceIndex]) return;

    const timing = sentenceTimings[sentenceIndex];
    setActiveSentenceIndex(sentenceIndex);

    // Clear any auto-advance timer
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }

    player.currentTime = timing.startTime;
    player.playbackRate = playbackSpeed;
    player.play();
  }, [sentenceTimings, playbackSpeed]);

  // Keep ref in sync so handleTimeUpdate's auto-advance can call it
  playSentenceRef.current = playSentence;

  // Toggle shadow mode
  const toggleShadowMode = useCallback(() => {
    const next = !shadowMode;
    setShadowMode(next);
    if (next) {
      // Build timings when entering shadow mode
      const timings = buildSentenceTimings();
      setSentenceTimings(timings);
      setPlayedSentences(new Set());
      setActiveSentenceIndex(-1);
      // Pause any currently playing audio
      if (audioPlayerRef.current && !audioPlayerRef.current.paused) {
        audioPlayerRef.current.pause();
      }
    } else {
      // Reset shadow state when exiting
      setActiveSentenceIndex(-1);
      setPlayedSentences(new Set());
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
        autoAdvanceTimerRef.current = null;
      }
    }
  }, [shadowMode, buildSentenceTimings]);

  // Clean up auto-advance timer on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
      }
    };
  }, []);

      // Memoize text rendering to prevent unnecessary re-renders
  const renderedText = useMemo(() => {
    if (!currentPage || !currentPage.content) {
      return '';
    }

    // Strip [[vocab]] markers from AI output, keep the word
    let text = cleanMarkdown(currentPage.content);
    const vocabMarkerWords = [];
    text = text.replace(/\[\[([^\]]+)\]\]/g, (_, word) => {
      vocabMarkerWords.push(word.toLowerCase());
      return word;
    });

    // Merge marker-detected vocab with user-provided vocab list
    const allVocab = [...new Set([...(vocabWords || []).map(v => v.toLowerCase()), ...vocabMarkerWords])];

    // Shadow mode: sentence-level rendering with per-sentence audio control
    if (shadowMode && audioUrl && sentenceTimings.length > 0) {
      const paragraphs = text.split(/\n\n+/).filter(Boolean);
      let globalSentenceIdx = 0;

      return paragraphs.map((para, pIdx) => {
        const sentences = splitIntoSentences(para);
        return (
          <div key={pIdx} className="mb-4">
            {sentences.map((sentence, sIdx) => {
              const sGlobalIdx = globalSentenceIdx++;
              const timing = sentenceTimings[sGlobalIdx];
              const isActive = sGlobalIdx === activeSentenceIndex;
              const wasPlayed = playedSentences.has(sGlobalIdx);
              const isActiveAndPlaying = isActive && isPlaying;

              // Character-level highlighting within the active playing sentence
              let sentenceContent;
              if (isActiveAndPlaying && timing && audioTimestamps?.characters) {
                const { characters } = audioTimestamps;
                const startChar = timing.startCharIdx;
                const endChar = timing.endCharIdx;
                sentenceContent = (
                  <span>
                    {characters.slice(startChar, endChar + 1).map((char, cIdx) => {
                      const globalCharIdx = startChar + cIdx;
                      return (
                        <span
                          key={cIdx}
                          className={`${
                            globalCharIdx <= currentCharIndex
                              ? 'bg-yellow-200 text-yellow-900'
                              : ''
                          } transition-all duration-100`}
                        >
                          {char}
                        </span>
                      );
                    })}
                  </span>
                );
              } else {
                // Render words with vocab highlighting
                const words = sentence.split(/(\s+)/);
                sentenceContent = words.map((word, wIdx) => {
                  const clean = word.replace(/[.,!?;:"""''()[\]{}]/g, '').toLowerCase();
                  if (allVocab.length > 0 && allVocab.includes(clean)) {
                    return <span key={wIdx} className="underline decoration-2 decoration-gray-400 text-gray-900 font-semibold">{word}</span>;
                  }
                  return <React.Fragment key={wIdx}>{word}</React.Fragment>;
                });
              }

              return (
                <div
                  key={sIdx}
                  onClick={() => timing && playSentence(sGlobalIdx)}
                  className={`group relative cursor-pointer rounded-lg px-3 py-2 my-1 transition-all duration-200 ${
                    isActive
                      ? 'bg-gray-100 border-l-2 border-gray-900'
                      : isActiveAndPlaying
                        ? 'bg-gray-100 border-l-2 border-gray-900'
                        : 'hover:bg-gray-50 border-l-2 border-transparent'
                  } ${!isActive && !wasPlayed ? '' : ''} ${!isActive && activeSentenceIndex >= 0 ? 'opacity-50' : 'opacity-100'}`}
                >
                  <div className="flex items-start gap-2">
                    <span className={`mt-1 flex-shrink-0 transition-colors ${
                      isActive ? 'text-gray-900' : 'text-gray-300 group-hover:text-gray-500'
                    }`}>
                      {isActiveAndPlaying ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <rect x="6" y="4" width="4" height="16" rx="1" />
                          <rect x="14" y="4" width="4" height="16" rx="1" />
                        </svg>
                      ) : wasPlayed ? (
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      )}
                    </span>
                    <span className="flex-1">{sentenceContent}</span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      });
    }

    // During audio playback (non-shadow), use character/word highlighting (existing behavior)
    if (audioUrl && isPlaying && !shadowMode) {
      if (audioTimestamps && audioTimestamps.characters && Array.isArray(audioTimestamps.characters)) {
        const { characters } = audioTimestamps;
        return (
          <span>
            {characters.map((char, index) => (
              <span
                key={index}
                className={`${
                  index <= currentCharIndex
                    ? 'bg-yellow-200 text-yellow-900'
                    : ''
                } transition-all duration-150`}
              >
                {char}
              </span>
            ))}
          </span>
        );
      } else {
        const words = splitIntoWords(text) || [];
        wordsRef.current = words;
        return words.map((word, index) => {
          const isWhitespace = /^\s+$/.test(word);
          const isCurrentWord = !isWhitespace && index === currentWordIndex;
          return (
            <span
              key={index}
              className={`${isCurrentWord ? 'bg-yellow-200 text-yellow-900' : ''} transition-all duration-200`}
            >
              {word}
            </span>
          );
        });
      }
    }

    // Not playing audio — render with sentence clicks (T3) + vocab highlights (T4)
    const paragraphs = text.split(/\n\n+/).filter(Boolean);

    return paragraphs.map((para, pIdx) => {
      const sentences = splitIntoSentences(para);
      return (
        <p key={pIdx} className="mb-4">
          {sentences.map((sentence, sIdx) => {
            // Render sentence with vocab word highlighting
            const words = sentence.split(/(\s+)/);
            const rendered = words.map((word, wIdx) => {
              const clean = word.replace(/[.,!?;:"""''()[\]{}]/g, '').toLowerCase();
              if (allVocab.length > 0 && allVocab.includes(clean)) {
                return <span key={wIdx} className="underline decoration-2 decoration-gray-400 text-gray-900 font-semibold">{word}</span>;
              }
              return <React.Fragment key={wIdx}>{word}</React.Fragment>;
            });

            if (isLearningMode) {
              return (
                <span
                  key={sIdx}
                  className="clickable-sentence cursor-pointer hover:bg-gray-100 rounded px-0.5 transition-colors"
                  onClick={(e) => handleSentenceClick(sentence, e)}
                  title="Click to translate"
                >
                  {rendered}
                  {sIdx < sentences.length - 1 ? ' ' : ''}
                </span>
              );
            }
            return <React.Fragment key={sIdx}>{rendered}{sIdx < sentences.length - 1 ? ' ' : ''}</React.Fragment>;
          })}
        </p>
      );
    });
  }, [currentPage?.content, audioUrl, audioTimestamps, currentCharIndex, currentWordIndex, isPlaying, isLearningMode, vocabWords, handleSentenceClick, shadowMode, sentenceTimings, activeSentenceIndex, playedSentences, playSentence]);

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-md" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  const handleSubmit = (e, choiceIndex) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setSelectedChoiceIndex(choiceIndex);

    // Small delay to show the loading state before navigating
    setTimeout(() => {
      // Create form and submit it
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `/adventure_story_pages/${currentPage.id}/choose_path`;

      // Add CSRF token
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = formToken;
      form.appendChild(csrfInput);

      // Add choice index
      const choiceInput = document.createElement('input');
      choiceInput.type = 'hidden';
      choiceInput.name = 'choice_index';
      choiceInput.value = choiceIndex;
      form.appendChild(choiceInput);

      document.body.appendChild(form);
      form.submit();
    }, 300);
  };

  const handleCustomSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;

    const customChoice = e.target.elements.custom_choice.value.trim();
    if (!customChoice) return;

    setSubmitting(true);
    setSelectedChoiceIndex('custom');

    setTimeout(() => {
      // Create form and submit it
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `/adventure_story_pages/${currentPage.id}/choose_path`;

      // Add CSRF token
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = formToken;
      form.appendChild(csrfInput);

      // Add custom choice
      const choiceInput = document.createElement('input');
      choiceInput.type = 'hidden';
      choiceInput.name = 'custom_choice';
      choiceInput.value = customChoice;
      form.appendChild(choiceInput);

      document.body.appendChild(form);
      form.submit();
    }, 300);
  };

  const navigateToPage = (pageId) => {
    window.location.href = `/adventure_story_pages/${pageId}`;
  };

  // Handle ending the book - generates final page
  const handleEndBook = async (endingType = 'auto', customEndingText = '') => {
    if (!canEnd || endingBook) return;

    setEndingBook(true);
    setShowEndOptions(false);

    try {
      const response = await axios.post(
        `/adventure_stories/${storyId}/choose_path`,
        {
          ending_type: endingType,
          custom_ending: customEndingText,
          current_page: currentPage.page_number
        },
        {
          headers: {
            "X-CSRF-Token": formToken,
            "Content-Type": "application/json"
          }
        }
      );

      // Redirect to the new final page
      if (response.data.pages && response.data.pages.length > 0) {
        const finalPage = response.data.pages[response.data.pages.length - 1];
        window.location.href = `/adventure_story_pages/${finalPage.id}`;
      } else {
        // Fallback - reload the page
        window.location.reload();
      }
    } catch (error) {
      console.error("Error ending book:", error);
      alert("Something went wrong. Please try again.");
      setEndingBook(false);
    }
  };

  // Handle sharing the book
  const handleShareBook = async () => {
    if (!isCompleted || sharingBook) return;

    setSharingBook(true);
    try {
      const response = await axios.patch(
        `/adventure_stories/${storyId}/share`,
        {},
        {
          headers: {
            "X-CSRF-Token": formToken,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.success) {
        setIsPublic(true);
      }
    } catch (error) {
      console.error("Error sharing book:", error);
      alert("Something went wrong. Please try again.");
    }
    setSharingBook(false);
  };

  // Narrator Picker Component
  const NarratorPicker = () => {
    if (!showNarratorPicker) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden">
          <div className="bg-gray-900 p-4 text-white">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Choose a Narrator</h3>
              <button
                onClick={() => {
                  setShowNarratorPicker(false);
                  if (previewAudioRef.current) {
                    previewAudioRef.current.pause();
                  }
                  setPreviewAudioUrl(null);
                }}
                className="text-white/80 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-white/80 text-sm mt-1">Select who will read your story aloud</p>
          </div>

          <div className="p-4 overflow-y-auto max-h-[60vh]">
            {/* Default Voice Option */}
            <button
              onClick={() => handleNarratorSelect(null)}
              disabled={narratorChanging}
              className={`w-full p-3 mb-2 rounded-lg border-2 transition-all text-left flex items-center ${
                !selectedNarrator
                  ? 'border-gray-900 bg-gray-50'
                  : 'border-gray-200 hover:border-gray-900'
              } ${narratorChanging ? 'opacity-50' : ''}`}
            >
              <i className="fa-solid fa-microphone text-gray-900 text-lg mr-3"></i>
              <div className="flex-1">
                <div className="font-medium">Default Voice</div>
                <div className="text-sm text-gray-500">Standard narrator</div>
              </div>
              {!selectedNarrator && (
                <svg className="w-5 h-5 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>

            {/* Narrator List */}
            {narrators.map((narrator) => (
              <div
                key={narrator.id}
                className={`w-full p-3 mb-2 rounded-lg border-2 transition-all ${
                  selectedNarrator === narrator.id
                    ? 'border-gray-900 bg-gray-50'
                    : 'border-gray-200 hover:border-gray-900'
                } ${narratorChanging ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center">
                  <button
                    onClick={() => handleNarratorSelect(narrator)}
                    disabled={narratorChanging}
                    className="flex-1 flex items-center text-left"
                  >
                    <span className="text-2xl mr-3">{narrator.flag}</span>
                    <div className="flex-1">
                      <div className="font-medium flex items-center gap-2">
                        {narrator.name}
                        {narrator.isSystem && (
                          <span className="bg-green-100 text-green-700 text-xs px-1.5 py-0.5 rounded">System</span>
                        )}
                        {narrator.isDefault && (
                          <span className="bg-yellow-100 text-yellow-700 text-xs px-1.5 py-0.5 rounded">Default</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {narrator.gender ? `${narrator.gender.charAt(0).toUpperCase() + narrator.gender.slice(1)} voice` : narrator.language.charAt(0).toUpperCase() + narrator.language.slice(1)}
                      </div>
                    </div>
                    {selectedNarrator === narrator.id && (
                      <svg className="w-5 h-5 text-gray-900 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                  {narrator.hasPreview && narrator.previewUrl && (
                    <button
                      onClick={() => playNarratorPreview(narrator.previewUrl)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition"
                      title="Preview voice"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      </svg>
                    </button>
                  )}
                </div>
                {narrator.description && (
                  <p className="text-xs text-gray-500 mt-1 ml-11">{narrator.description}</p>
                )}
              </div>
            ))}

            {narrators.length === 0 && (
              <div className="text-center text-gray-500 py-4">
                No custom narrators available yet.
              </div>
            )}
          </div>

          {/* Preview Audio Element */}
          {previewAudioUrl && (
            <audio
              ref={previewAudioRef}
              src={previewAudioUrl}
              onEnded={() => setPreviewAudioUrl(null)}
            />
          )}
        </div>
      </div>
    );
  };

  // Generate audio button
  const GenerateAudioButton = () => {
    if (loading || currentPage.status !== 'completed') {
      return null;
    }

    // Narrator selector button
    const NarratorButton = () => (
      <button
        type="button"
        onClick={() => setShowNarratorPicker(true)}
        className="flex items-center bg-white/80 hover:bg-white text-gray-700 py-2 px-3 rounded-lg shadow-sm border border-gray-200 transition"
        title="Change narrator"
      >
        <span className="mr-1">{narratorFlag || <i className="fa-solid fa-microphone text-gray-500"></i>}</span>
        <span className="text-sm hidden sm:inline">{narratorName || 'Default'}</span>
        <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    );

    if (audioUrl) {
      // Show regenerate button if we have audio but no timestamps
      if (!audioTimestamps) {
        return (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="flex items-center bg-gray-900 hover:bg-gray-800 text-white py-2 px-4 rounded-lg transition"
              onClick={(e) => generateAudio(e, true)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="smooth-text">Regenerate with Word Tracking</span>
            </button>
            {narrators.length > 0 && <NarratorButton />}
          </div>
        );
      }
      // Show narrator selector even when audio exists (to change voice)
      if (narrators.length > 0) {
        return (
          <div className="mt-4 flex items-center gap-2">
            <NarratorButton />
            <button
              type="button"
              onClick={(e) => generateAudio(e, true)}
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center"
              title="Regenerate audio with current narrator"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Regenerate
            </button>
          </div>
        );
      }
      return null;
    }

    return (
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="flex items-center bg-gray-900 hover:bg-gray-800 text-white py-2 px-4 rounded-lg transition"
          onClick={generateAudio}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414-7.072m-2.829 9.9a9 9 0 010-12.728" />
          </svg>
          <span className="smooth-text">Read to me!</span>
        </button>
        {narrators.length > 0 && <NarratorButton />}
      </div>
    );
  };

  return (
    <div className="w-full mx-auto">
      {/* Narrator Picker Modal */}
      <NarratorPicker />

      <div className="flex flex-col md:flex-row bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200 max-w-screen-2xl mx-auto">
        {/* Left side - Story text */}
        <div className="md:w-1/2 p-6 md:p-8 font-serif relative bg-white">
          <div className="prose prose-lg max-w-none h-[calc(100vh-200px)] overflow-y-auto pr-4 custom-scrollbar">
            {loading && currentPage.status === 'generating_content' ? (
              <div className="flex items-center justify-center h-full w-full">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-gray-900"></div>
                <p className="ml-4 text-lg text-gray-700 font-medium smooth-text">Writing your story...</p>
              </div>
            ) : (
              <>
                {/* Learning mode toolbar (T5: translation toggle) */}
                {isLearningMode && (
                  <div className="mb-4 pb-3 border-b border-gray-200 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={toggleTranslation}
                        className={`text-sm px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
                          showTranslation
                            ? 'bg-gray-900 text-white shadow-sm'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                        </svg>
                        {showTranslation ? 'Hide Translation' : 'Show Translation'}
                      </button>
                      {audioUrl && audioTimestamps && (
                        <button
                          onClick={toggleShadowMode}
                          className={`text-sm px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
                            shadowMode
                              ? 'bg-gray-900 text-white shadow-sm'
                              : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                          }`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                          </svg>
                          Shadow
                        </button>
                      )}
                      <button
                        onClick={handleShareSnippet}
                        className="text-sm px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                        </svg>
                        {snippetCopied ? 'Link Copied!' : 'Share Page'}
                      </button>
                      {!shadowMode && <span className="text-xs text-gray-400 hidden sm:inline">Click any sentence to explain</span>}
                    </div>

                    {/* Shadow mode controls — inline, YouTube-style */}
                    {shadowMode && audioUrl && sentenceTimings.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 pt-1">
                        {/* Prev */}
                        <button
                          onClick={() => activeSentenceIndex > 0 && playSentence(activeSentenceIndex - 1)}
                          disabled={activeSentenceIndex <= 0}
                          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition disabled:opacity-25 disabled:cursor-not-allowed"
                          title="Previous sentence"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
                          </svg>
                        </button>

                        {/* Replay */}
                        <button
                          onClick={() => activeSentenceIndex >= 0 && playSentence(activeSentenceIndex)}
                          disabled={activeSentenceIndex < 0}
                          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition disabled:opacity-25 disabled:cursor-not-allowed"
                          title="Replay sentence"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>

                        {/* Next */}
                        <button
                          onClick={() => activeSentenceIndex < sentenceTimings.length - 1 && playSentence(activeSentenceIndex + 1)}
                          disabled={activeSentenceIndex >= sentenceTimings.length - 1}
                          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition disabled:opacity-25 disabled:cursor-not-allowed"
                          title="Next sentence"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M16 18h2V6h-2zM6 18l8.5-6L6 6z" />
                          </svg>
                        </button>

                        {/* Divider */}
                        <div className="w-px h-5 bg-gray-200 mx-0.5"></div>

                        {/* Speed selector */}
                        <div className="flex items-center bg-gray-100 rounded-lg overflow-hidden">
                          {[1.0, 0.75, 0.5].map((speed) => (
                            <button
                              key={speed}
                              onClick={() => {
                                setPlaybackSpeed(speed);
                                if (audioPlayerRef.current) {
                                  audioPlayerRef.current.playbackRate = speed;
                                }
                              }}
                              className={`px-2 py-1 text-xs font-medium transition-all ${
                                playbackSpeed === speed
                                  ? 'bg-gray-900 text-white'
                                  : 'text-gray-500 hover:text-gray-700'
                              }`}
                              title={`${speed}x speed`}
                            >
                              {speed === 1.0 ? '1x' : speed === 0.75 ? '.75x' : '.5x'}
                            </button>
                          ))}
                        </div>

                        {/* Divider */}
                        <div className="w-px h-5 bg-gray-200 mx-0.5"></div>

                        {/* Auto-advance */}
                        <button
                          onClick={() => setAutoAdvance(!autoAdvance)}
                          className={`p-1.5 rounded-lg transition ${
                            autoAdvance
                              ? 'bg-gray-900 text-white'
                              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                          }`}
                          title={autoAdvance ? 'Auto-advance on' : 'Auto-advance off'}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                          </svg>
                        </button>

                        {/* Progress pill */}
                        <div className="ml-auto flex items-center gap-1.5">
                          <span className="text-xs text-gray-400">
                            {playedSentences.size}/{sentenceTimings.length}
                          </span>
                          <div className="w-16 bg-gray-200 rounded-full h-1">
                            <div
                              className="bg-gray-900 h-1 rounded-full transition-all duration-300"
                              style={{ width: `${sentenceTimings.length > 0 ? (playedSentences.size / sentenceTimings.length) * 100 : 0}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="text-gray-800 leading-relaxed">
                  {renderedText}
                </div>

                {/* T5: Parallel translated paragraphs */}
                {showTranslation && isLearningMode && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    {translationLoading ? (
                      <div className="flex items-center text-gray-600 text-sm">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-500 border-t-transparent mr-2"></div>
                        Translating...
                      </div>
                    ) : translatedParagraphs ? (
                      <div className="text-gray-500 text-sm italic leading-relaxed space-y-2">
                        {translatedParagraphs.map((p, i) => (
                          <p key={i}>{p}</p>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )}

                {/* T3: Explain popover — rendered via portal to escape overflow clipping */}
                {explainPopover && createPortal(
                  <div
                    className="explain-popover fixed z-50 bg-white rounded-xl shadow-2xl border border-gray-200 p-4 max-w-sm"
                    style={{
                      left: Math.min(explainPopover.x, window.innerWidth - 340),
                      top: Math.min(explainPopover.y + 8, window.innerHeight - 300),
                      maxHeight: '60vh',
                      overflowY: 'auto'
                    }}
                  >
                    {explainPopover.loading ? (
                      <div className="flex items-center text-gray-600 text-sm">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-500 border-t-transparent mr-2"></div>
                        Translating...
                      </div>
                    ) : explainPopover.data ? (
                      <div className="space-y-2">
                        <div>
                          <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">Translation</div>
                          <div className="text-gray-800 font-medium">{explainPopover.data.translation}</div>
                        </div>
                        {explainPopover.data.grammar_note && (
                          <div>
                            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">Grammar</div>
                            <div className="text-gray-600 text-sm">{explainPopover.data.grammar_note}</div>
                          </div>
                        )}
                        {explainPopover.data.examples && explainPopover.data.examples.length > 0 && (
                          <div>
                            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">Examples</div>
                            {explainPopover.data.examples.map((ex, i) => (
                              <div key={i} className="text-sm text-gray-700 italic">{ex}</div>
                            ))}
                          </div>
                        )}
                        {explainPopover.data.key_words && explainPopover.data.key_words.length > 0 && (
                          <div>
                            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">Key Words</div>
                            <div className="flex flex-wrap gap-1.5 mt-1">
                              {explainPopover.data.key_words.map((kw, i) => (
                                <span key={i} className="inline-flex items-center bg-gray-50 text-gray-700 text-xs px-2 py-1 rounded-lg border border-gray-200">
                                  <span className="font-semibold">{kw.word}</span>
                                  <span className="text-gray-400 mx-1">=</span>
                                  <span>{kw.translation}</span>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        <button
                          onClick={() => setExplainPopover(null)}
                          className="text-xs text-gray-400 hover:text-gray-600 mt-1"
                        >
                          Close
                        </button>
                      </div>
                    ) : null}
                  </div>,
                  document.body
                )}

                {/* Audio controls */}
                <GenerateAudioButton />
                <AudioPlayer
                  ref={audioPlayerRef}
                  audioUrl={audioUrl}
                  audioLoading={audioLoading}
                  audioTimestamps={audioTimestamps}
                  onTimeUpdate={handleTimeUpdate}
                  onPlay={handlePlay}
                  onPause={handlePause}
                  onEnded={handleEnded}
                  onLoadedData={handleLoadedData}
                  currentPage={currentPage}
                />

                {/* Comprehension Questions - Learning Mode */}
                {isLearningMode && isSignedIn && comprehensionQuestions.length > 0 && (
                  <div className="mt-8 bg-white p-6 rounded-xl border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900 smooth-text flex items-center">
                        <i className="fa-solid fa-lightbulb w-5 h-5 mr-2 text-gray-500"></i>
                        Test Your Understanding
                      </h3>
                      <label className="flex items-center text-sm text-gray-600">
                        <input
                          type="checkbox"
                          checked={blockChoicesUntilAnswered}
                          onChange={(e) => setBlockChoicesUntilAnswered(e.target.checked)}
                          className="mr-2 rounded text-gray-900"
                        />
                        Block choices until answered
                      </label>
                    </div>

                    {/* Proficiency Display */}
                    {userProficiency && (
                      <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-gray-800">Level: {userProficiency.level}</span>
                          <span className="text-gray-600">{userProficiency.xp_points} XP</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div className="bg-gray-900 h-2 rounded-full" style={{ width: `${(userProficiency.xp_points % 100)}%` }}></div>
                        </div>
                      </div>
                    )}

                    {comprehensionLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900"></div>
                        <span className="ml-3 text-gray-700">Loading questions...</span>
                      </div>
                    ) : !allQuestionsAnswered ? (
                      <div className="space-y-4">
                        {comprehensionQuestions.map((q, idx) => (
                          questionsAnswered[q.id]?.answered ? null : (
                            <div key={q.id} className={`p-4 bg-white rounded-lg border ${idx === currentQuestionIndex ? 'border-gray-900 shadow-sm' : 'border-gray-200 opacity-50'}`}>
                              <p className="font-medium text-gray-800 mb-3">{q.question_text}</p>

                              {q.question_type === 'multiple_choice' && q.choices && (
                                <div className="space-y-2">
                                  {q.choices.map((choice, cIdx) => (
                                    <button
                                      key={cIdx}
                                      onClick={() => submitComprehensionAnswer(q.id, choice.text)}
                                      disabled={answerFeedback !== null}
                                      className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-gray-900 hover:bg-gray-50 transition-colors disabled:opacity-50"
                                    >
                                      {choice.text}
                                    </button>
                                  ))}
                                </div>
                              )}

                              {(q.question_type === 'fill_blank' || (q.question_type !== 'multiple_choice' && !q.choices)) && (
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={userAnswer}
                                    onChange={(e) => setUserAnswer(e.target.value)}
                                    placeholder="Type your answer..."
                                    className="flex-1 p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
                                    onKeyDown={(e) => e.key === 'Enter' && submitComprehensionAnswer(q.id, userAnswer)}
                                  />
                                  <button
                                    onClick={() => submitComprehensionAnswer(q.id, userAnswer)}
                                    className="px-4 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
                                  >
                                    Check
                                  </button>
                                </div>
                              )}

                              {/* Fallback: unrecognized type but has choices — render as multiple choice */}
                              {q.question_type !== 'multiple_choice' && q.question_type !== 'fill_blank' && q.choices && q.choices.length > 0 && (
                                <div className="space-y-2">
                                  {q.choices.map((choice, cIdx) => (
                                    <button
                                      key={cIdx}
                                      onClick={() => submitComprehensionAnswer(q.id, choice.text)}
                                      disabled={answerFeedback !== null}
                                      className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-gray-900 hover:bg-gray-50 transition-colors disabled:opacity-50"
                                    >
                                      {choice.text}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 bg-gray-50 rounded-lg border border-gray-200">
                        <i className="fa-solid fa-circle-check text-4xl text-gray-900 mb-2"></i>
                        <p className="text-gray-900 font-medium">All questions answered!</p>
                        <p className="text-gray-600 text-sm">You can now continue the story.</p>
                      </div>
                    )}

                    {/* Answer Feedback */}
                    {answerFeedback && (
                      <div className={`mt-4 p-4 rounded-lg ${answerFeedback.correct ? 'bg-gray-50 border border-gray-300' : 'bg-white border border-gray-300'}`}>
                        <p className={`font-medium flex items-center gap-2 ${answerFeedback.correct ? 'text-gray-900' : 'text-gray-700'}`}>
                          {answerFeedback.correct
                            ? <><i className="fa-solid fa-check text-gray-900"></i> Correct!</>
                            : <><i className="fa-solid fa-xmark text-gray-500"></i> Not quite right</>}
                        </p>
                        {!answerFeedback.correct && answerFeedback.correctAnswer && (
                          <p className="text-gray-600 text-sm mt-1">Correct answer: {answerFeedback.correctAnswer.answer}</p>
                        )}
                        {answerFeedback.correct && answerFeedback.xpEarned > 0 && (
                          <p className="text-gray-700 text-sm mt-1">+{answerFeedback.xpEarned} XP earned!</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Discussion Chat - Page Tutor */}
                {isLearningMode && isSignedIn && (
                  <div className="mt-8">
                    {!showDiscussion ? (
                      <button
                        onClick={startDiscussion}
                        className="w-full bg-gray-900 hover:bg-gray-800 text-white font-medium py-4 px-6 rounded-xl transition-all duration-200"
                      >
                        <div className="flex items-center justify-center">
                          <i className="fa-solid fa-comments w-5 h-5 mr-2"></i>
                          <span>Discuss This Page</span>
                        </div>
                        <p className="text-gray-400 text-xs mt-1">Ask anything about vocabulary, grammar, or meaning</p>
                      </button>
                    ) : (
                      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        {/* Header */}
                        <div className="bg-gray-900 px-4 py-3 flex items-center justify-between">
                          <div className="flex items-center text-white">
                            <i className="fa-solid fa-comments w-5 h-5 mr-2"></i>
                            <span className="font-medium">Page Discussion</span>
                          </div>
                          <button
                            onClick={() => setShowDiscussion(false)}
                            className="text-white/80 hover:text-white transition"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>

                        {/* Messages area */}
                        <div className="max-h-96 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                          {discussionInitializing ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gray-900"></div>
                              <span className="ml-2 text-gray-600 text-sm">Starting discussion...</span>
                            </div>
                          ) : discussionMessages.length === 0 ? (
                            <div className="text-center py-6">
                              <p className="text-gray-500 text-sm mb-4">Ask anything about this page!</p>
                              <div className="flex flex-wrap justify-center gap-2">
                                {['What does this page mean?', 'Explain the vocabulary', 'Help me with the grammar'].map((suggestion) => (
                                  <button
                                    key={suggestion}
                                    onClick={() => sendDiscussionMessage(suggestion)}
                                    className="text-xs bg-gray-50 hover:bg-gray-100 text-gray-700 px-3 py-2 rounded-full border border-gray-200 transition"
                                  >
                                    {suggestion}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <>
                              {discussionMessages.map((msg, idx) => (
                                <div key={msg.id || idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                  <div className={`max-w-[85%] rounded-lg px-4 py-2.5 ${
                                    msg.role === 'user'
                                      ? 'bg-gray-900 text-white'
                                      : 'bg-gray-50 border border-gray-200 text-gray-800'
                                  }`}>
                                    <p className="text-sm whitespace-pre-wrap">
                                      {msg.role === 'assistant' && msg.structured_response?.explanation
                                        ? msg.structured_response.explanation
                                        : msg.content}
                                    </p>

                                    {/* Vocabulary highlights for assistant messages */}
                                    {msg.role === 'assistant' && msg.vocabulary_highlight && msg.vocabulary_highlight.length > 0 && (
                                      <div className="mt-2 flex flex-wrap gap-1.5">
                                        {msg.vocabulary_highlight.map((v, vIdx) => (
                                          <span key={vIdx} className="inline-block bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">
                                            <strong>{v.word}</strong> = {v.translation}
                                          </span>
                                        ))}
                                      </div>
                                    )}

                                    {/* Follow-up questions for assistant messages */}
                                    {msg.role === 'assistant' && msg.follow_up_questions && msg.follow_up_questions.length > 0 && (
                                      <div className="mt-2 flex flex-wrap gap-1.5">
                                        {msg.follow_up_questions.map((fq, fqIdx) => (
                                          <button
                                            key={fqIdx}
                                            onClick={() => sendDiscussionMessage(fq)}
                                            className="text-xs bg-white hover:bg-gray-50 text-gray-600 px-2 py-1 rounded-full border border-gray-200 transition"
                                          >
                                            {fq}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                              {discussionLoading && (
                                <div className="flex justify-start">
                                  <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5">
                                    <div className="flex items-center text-sm text-gray-600">
                                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent mr-2"></div>
                                      Thinking...
                                    </div>
                                  </div>
                                </div>
                              )}
                              <div ref={discussionEndRef} />
                            </>
                          )}
                        </div>

                        {/* Input bar */}
                        {!discussionInitializing && (
                          <div className="border-t border-gray-200 p-3 flex gap-2">
                            <input
                              type="text"
                              value={discussionInput}
                              onChange={(e) => setDiscussionInput(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendDiscussionMessage()}
                              placeholder="Ask about this page..."
                              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
                              disabled={discussionLoading}
                            />
                            <button
                              onClick={() => sendDiscussionMessage()}
                              disabled={discussionLoading || !discussionInput.trim()}
                              className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Send
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {currentPage.choices && currentPage.choices.length > 0 && (
                  <div className="mt-10 bg-gray-50 p-6 rounded-xl border border-gray-200">
                    {/* End the Story Section - show FIRST after 5+ pages */}
                    {canEnd && (
                      <div className="mb-6 pb-6 border-b border-gray-200 bg-white -mx-6 -mt-6 px-6 pt-6 rounded-t-xl">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-lg font-semibold text-gray-900 smooth-text flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                            Ready to finish your book?
                          </h3>
                          <button
                            onClick={() => setShowEndOptions(!showEndOptions)}
                            disabled={submitting || endingBook}
                            className="bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg font-medium flex items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {showEndOptions ? 'Hide options' : 'End the Story'}
                            <svg className={`w-4 h-4 ml-1 transition-transform ${showEndOptions ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>

                        {showEndOptions && (
                          <div className="space-y-3">
                            {/* Auto-end option */}
                            <button
                              onClick={() => handleEndBook('auto')}
                              disabled={endingBook || submitting}
                              className="w-full p-4 bg-white hover:bg-gray-50 border-2 border-gray-300 rounded-xl transition text-left disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <div className="flex items-center">
                                <div className="bg-gray-100 rounded-full p-2 mr-3">
                                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                  </svg>
                                </div>
                                <div>
                                  <div className="font-semibold text-gray-900">End it for me</div>
                                  <div className="text-sm text-gray-600">Let the story write a perfect ending</div>
                                </div>
                                {endingBook && (
                                  <div className="ml-auto">
                                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-900 border-t-transparent"></div>
                                  </div>
                                )}
                              </div>
                            </button>

                            {/* Custom ending option */}
                            <div className="p-4 bg-white border-2 border-gray-300 rounded-xl">
                              <div className="flex items-center mb-3">
                                <div className="bg-gray-100 rounded-full p-2 mr-3">
                                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </div>
                                <div>
                                  <div className="font-semibold text-gray-900">Write your own ending</div>
                                  <div className="text-sm text-gray-600">Tell us how you want the story to end</div>
                                </div>
                              </div>
                              <textarea
                                value={customEnding}
                                onChange={(e) => setCustomEnding(e.target.value)}
                                placeholder="How should your story end? (e.g., 'They all became best friends and lived happily ever after')"
                                className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
                                rows={2}
                                disabled={endingBook || submitting}
                              />
                              <button
                                onClick={() => handleEndBook('custom', customEnding)}
                                disabled={endingBook || !customEnding.trim() || submitting}
                                className="mt-3 w-full py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                              >
                                {endingBook ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                    Creating ending...
                                  </>
                                ) : 'Finish My Story'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <h3 className="text-xl font-bold mb-4 text-gray-900 smooth-text">
                      {canCreatePaths ? 'Choose Your Path' : 'Follow the Story'}
                      {isLearningMode && blockChoicesUntilAnswered && comprehensionQuestions.length > 0 && !allQuestionsAnswered && (
                        <span className="text-xs font-normal text-red-400 ml-2">
                          (answer questions first)
                        </span>
                      )}
                    </h3>
                    <div className="space-y-3">
                      {currentPage.choices.map((choice, index) => {
                        // Check if this choice has already been taken
                        const takenChoice = takenChoices[index];
                        const isSelected = selectedChoiceIndex === index;
                        // Block choices if learning mode requires questions answered first
                        const needsQuestions = isLearningMode && blockChoicesUntilAnswered && comprehensionQuestions.length > 0 && !allQuestionsAnswered;
                        const isDisabled = submitting || endingBook || needsQuestions;

                        if (takenChoice) {
                          // This is a choice that has already been taken - anyone can follow it
                          const isStoryOwnerBranch = takenChoice.creator_id === storyOwnerId;
                          const isMyBranch = takenChoice.creator_id === currentUserId;

                          return (
                            <div key={index}>
                              <div className="flex items-center gap-2">
                                <a
                                  href={isDisabled ? undefined : `/adventure_story_pages/${takenChoice.id}`}
                                  onClick={(e) => isDisabled && e.preventDefault()}
                                  className={`block flex-1 bg-gray-900 hover:bg-gray-800 text-white font-medium py-3 px-4 rounded-lg text-left transition-all duration-200 smooth-text ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="flex-1">{choice}</span>
                                    <div className="flex items-center gap-2 ml-2">
                                      {isMyBranch ? (
                                        <span className="bg-white/20 text-white/90 text-xs px-2 py-0.5 rounded">Your path</span>
                                      ) : isStoryOwnerBranch ? (
                                        <span className="text-white/70 text-xs">Original</span>
                                      ) : (
                                        <span className="text-white/70 text-xs">by {takenChoice.creator_name}</span>
                                      )}
                                      <span className="text-white/80">→</span>
                                    </div>
                                  </div>
                                </a>
                                {isLearningMode && (
                                  <button
                                    onClick={() => toggleChoiceTranslation(index)}
                                    className="clickable-sentence shrink-0 p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition"
                                    title="Translate this choice"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                              {(showTranslation || expandedChoiceTranslations[index]) && translatedChoices && translatedChoices[index] && (
                                <div className="text-xs text-gray-500 italic mt-1 ml-1 pl-3 border-l-2 border-gray-200">{translatedChoices[index]}</div>
                              )}
                            </div>
                          );
                        }

                        // This is a choice that hasn't been taken yet
                        // Only members can take new paths
                        if (!canCreatePaths) {
                          return (
                            <div key={index}>
                              <div className="w-full bg-gray-200 text-gray-500 font-medium py-3 px-4 rounded-lg text-left cursor-not-allowed">
                                <div className="flex items-center">
                                  <span className="flex-1">{choice}</span>
                                  <span className="ml-2 bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-1 rounded">
                                    Members Only
                                  </span>
                                </div>
                              </div>
                              {(showTranslation || expandedChoiceTranslations[index]) && translatedChoices && translatedChoices[index] && (
                                <div className="text-xs text-gray-500 italic mt-1 ml-1 pl-3 border-l-2 border-gray-200">{translatedChoices[index]}</div>
                              )}
                            </div>
                          );
                        }

                        return (
                          <div key={index}>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => handleSubmit(e, index)}
                                disabled={isDisabled}
                                className={`flex-1 bg-gray-900 hover:bg-gray-800 text-white font-medium py-3 px-4 rounded-lg text-left transition-all duration-200 smooth-text disabled:opacity-50 disabled:cursor-not-allowed ${isSelected ? 'ring-2 ring-gray-900 ring-offset-2' : ''}`}
                              >
                                <div className="flex items-center">
                                  <span className="flex-1">{choice}</span>
                                  {isSelected && (
                                    <div className="ml-2 animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                  )}
                                </div>
                              </button>
                              {isLearningMode && (
                                <button
                                  onClick={() => toggleChoiceTranslation(index)}
                                  className="clickable-sentence shrink-0 p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition"
                                  title="Translate this choice"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            {(showTranslation || expandedChoiceTranslations[index]) && translatedChoices && translatedChoices[index] && (
                              <div className="text-xs text-gray-500 italic mt-1 ml-1 pl-3 border-l-2 border-gray-200">{translatedChoices[index]}</div>
                            )}
                          </div>
                        );
                      })}

                      {/* Custom paths section */}
                      {customPaths && customPaths.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <h4 className="text-md font-semibold mb-3 text-gray-700 smooth-text">
                            Creative paths by other readers:
                          </h4>
                          <div className="space-y-2">
                            {customPaths.map((path, index) => {
                              const isDisabled = submitting || endingBook;
                              const isMyPath = path.creator_id === currentUserId;

                              return (
                                <a
                                  key={`custom-${index}`}
                                  href={isDisabled ? undefined : `/adventure_story_pages/${path.id}`}
                                  onClick={(e) => isDisabled && e.preventDefault()}
                                  className={`block w-full bg-gray-800 hover:bg-gray-700 text-white font-medium py-3 px-4 rounded-lg text-left transition-all duration-200 smooth-text ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="flex-1 italic">"{path.text}"</span>
                                    <div className="flex items-center gap-2 ml-2">
                                      {isMyPath ? (
                                        <span className="bg-white/20 text-white/90 text-xs px-2 py-0.5 rounded">Your path</span>
                                      ) : (
                                        <span className="text-white/70 text-xs">by {path.creator_name}</span>
                                      )}
                                      <span className="text-white/80">→</span>
                                    </div>
                                  </div>
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Custom path creation - members only */}
                    {canCreatePaths ? (
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <h3 className="text-lg font-semibold mb-3 text-gray-900 smooth-text">Or write your own path:</h3>
                        <form onSubmit={handleCustomSubmit} className="flex">
                          <input
                            type="text"
                            name="custom_choice"
                            placeholder="Write your own adventure path..."
                            className="flex-1 px-4 py-3 border border-gray-200 rounded-l-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                            required
                            disabled={submitting || endingBook}
                          />
                          <button
                            type="submit"
                            disabled={submitting || endingBook}
                            className="px-5 py-3 bg-gray-900 text-white rounded-r-lg font-medium hover:bg-gray-800 transition-colors duration-200 smooth-text disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                          >
                            {selectedChoiceIndex === 'custom' ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                Going...
                              </>
                            ) : 'Continue'}
                          </button>
                        </form>
                      </div>
                    ) : (
                      <div className="mt-8 pt-6 border-t border-gray-200">
                        <div className="bg-gray-50 rounded-xl p-5 text-center border border-gray-200">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            Want to choose your own adventure?
                          </h3>
                          <p className="text-gray-600 text-sm mb-4">
                            Sign up to create your own paths and write your own stories!
                          </p>
                          <a
                            href="/users/sign_up"
                            className="inline-block bg-gray-900 text-white px-6 py-2 rounded-lg font-medium hover:bg-gray-800 transition-colors"
                          >
                            Create Free Account
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right side - Image */}
        <div className="md:w-1/2 bg-gray-50 p-6 flex items-center justify-center h-[calc(100vh-200px)] overflow-hidden border-l border-gray-200">
          {loading && !currentPage.image_url ? (
            <div className="flex flex-col items-center justify-center h-full w-full">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-900 border-t-transparent mb-4"></div>
              <p className="text-lg text-gray-700 font-medium smooth-text">
                Drawing your illustration...
              </p>
            </div>
          ) : (
            currentPage.image_url ? (
              <div className="w-full h-full flex items-center justify-center relative group p-2">
                <img
                  src={currentPage.image_url}
                  alt="Story illustration"
                  className="max-h-full max-w-full rounded-xl shadow-lg border border-gray-200 object-contain transform transition-transform duration-700 group-hover:scale-[1.02]"
                />
              </div>
            ) : (
              <div className="text-gray-500 text-center p-10 w-full h-full flex items-center justify-center">
                <p className="smooth-text text-xl">Illustration will appear here</p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

StoryBook.propTypes = {
  page: PropTypes.object.isRequired,
  formToken: PropTypes.string.isRequired,
  takenChoices: PropTypes.object,
  customPaths: PropTypes.array,
  storyId: PropTypes.number,
  storyOwnerId: PropTypes.number,
  canEnd: PropTypes.bool,
  pageCount: PropTypes.number,
  isCompleted: PropTypes.bool,
  isPublic: PropTypes.bool,
  canCreatePaths: PropTypes.bool,
  isOwner: PropTypes.bool,
  isSignedIn: PropTypes.bool,
  currentUserId: PropTypes.number,
  narrators: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    flag: PropTypes.string,
    language: PropTypes.string,
    description: PropTypes.string,
    gender: PropTypes.string,
    isSystem: PropTypes.bool,
    isDefault: PropTypes.bool,
    hasPreview: PropTypes.bool,
    previewUrl: PropTypes.string
  })),
  currentNarratorId: PropTypes.number,
  currentNarratorName: PropTypes.string,
  currentNarratorFlag: PropTypes.string,
  targetLanguage: PropTypes.string,
  baseLanguage: PropTypes.string,
  vocabWords: PropTypes.array,
  isLearningMode: PropTypes.bool,
  pathDepth: PropTypes.number
};

export default StoryBook;